import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Input } from 'antd';
import { SplitShell } from '../layout/split/SplitShell';
import styles from '../layout/split/split.module.css';
import { ApiError, accountsApi } from '../api';

/**
 * /login/otp — email → 6-digit code → session.
 *
 * The code is emitted to the server log in dev (see log line `otp_issued` with
 * `code_dev_only`). When comms lands, the same code goes out via email/SMS.
 */
export function OtpPage() {
  return (
    <SplitShell>
      <OtpForm />
    </SplitShell>
  );
}

function OtpForm() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<'request' | 'verify'>('request');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function request(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !email) return;
    setBusy(true); setError(null);
    try {
      await accountsApi.requestOtp({ email });
      setStage('verify');
    } catch (err) {
      setError(errMessage(err));
    } finally { setBusy(false); }
  }

  async function verify(e: React.FormEvent) {
    e.preventDefault();
    if (busy || !email || code.length !== 6) return;
    setBusy(true); setError(null);
    try {
      const res = await accountsApi.verifyOtp({ email, code });
      window.location.href = res.redirect_to;
    } catch (err) {
      setError(errMessage(err));
    } finally { setBusy(false); }
  }

  return (
    <div>
      <div className={styles.stepHead}>
        <h2 className={styles.stepTitle}>{stage === 'request' ? 'One-time code' : 'Enter your code'}</h2>
        <p className={styles.stepSubtitle}>
          {stage === 'request'
            ? 'We\'ll email you a 6-digit code — no password required.'
            : `Enter the code we sent to ${email}. Valid for 5 minutes.`}
        </p>
      </div>

      <div style={{ marginTop: 22 }}>
        {stage === 'request' ? (
          <form className={styles.fields} onSubmit={request}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="otp-email">Email</label>
              <Input id="otp-email" size="large" type="email" autoComplete="email"
                placeholder="you@example.in" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            {error && <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />}
            <Button type="primary" htmlType="submit" size="large" block loading={busy} disabled={!email}>
              Send code
            </Button>
          </form>
        ) : (
          <form className={styles.fields} onSubmit={verify}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor="otp-code">Code</label>
              <Input id="otp-code" size="large" inputMode="numeric" pattern="\d{6}" maxLength={6}
                placeholder="123456" value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace", letterSpacing: 4 }} />
              <span className={styles.hint}>
                Didn't get it? <button type="button" className={styles.inlineLink}
                  onClick={() => { setStage('request'); setCode(''); }}>Send another</button>
              </span>
            </div>
            {error && <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />}
            <Button type="primary" htmlType="submit" size="large" block loading={busy}
              disabled={code.length !== 6}>
              Sign in
            </Button>
          </form>
        )}
      </div>

      <p className={styles.altAction}>
        Prefer a password? <Link to="/login">Password sign-in</Link>
      </p>
    </div>
  );
}

function errMessage(err: unknown): string {
  return err instanceof ApiError
    ? (err.body as { message?: string } | null)?.message ?? err.message
    : err instanceof Error ? err.message : String(err);
}
